/**
 * MonthNavigation
 *
 * 월 네비게이션 컴포넌트
 * - 현재 월 표시
 * - 이전/다음 달 이동 버튼
 * - 필터 버튼
 * - 리스트/달력 뷰 전환 버튼
 */

type ViewMode = 'list' | 'calendar'

/**
 * 테마 색상 정의
 * 나중에 전역 테마로 추출 가능
 */
const THEME_COLORS = {
  accent: {
    background: 'bg-primary-600',
    text: 'text-white',
    hover: 'hover:bg-primary-700',
  },
} as const

/**
 * Format month/year text based on screen size
 * Mobile: "2026.1" - Compact format
 * Desktop: "2026년 1월" - Full Korean format
 */
function formatMonthYear(year: number, month: number) {
  return {
    full: `${year}년 ${month}월`,
    compact: `${year}.${month}`,
  }
}

interface MonthNavigationProps {
  year: number
  month: number
  onPrevMonth: () => void
  onNextMonth: () => void
  onFilterClick?: () => void
  hasActiveFilters?: boolean
  viewMode?: ViewMode
  onViewModeChange?: (mode: ViewMode) => void
}

export function MonthNavigation({
  year,
  month,
  onPrevMonth,
  onNextMonth,
  onFilterClick,
  hasActiveFilters = false,
  viewMode = 'list',
  onViewModeChange,
}: MonthNavigationProps) {
  return (
    <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-1.5">
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-1 sm:gap-2">
        {/* 월 이동 */}
        <div className="flex items-center gap-2 sm:gap-4">
          <button
            onClick={onPrevMonth}
            aria-label="이전 달로 이동"
            className="min-h-12 min-w-12 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            ◀
          </button>
          {/* Full format for tablet+ (hidden on mobile) */}
          <span className="hidden sm:inline text-lg font-semibold text-gray-900 dark:text-gray-100 text-center whitespace-nowrap">
            {formatMonthYear(year, month).full}
          </span>

          {/* Compact format for mobile only */}
          <span className="sm:hidden text-lg font-semibold text-gray-900 dark:text-gray-100 text-center whitespace-nowrap">
            {formatMonthYear(year, month).compact}
          </span>
          <button
            onClick={onNextMonth}
            aria-label="다음 달로 이동"
            className="min-h-12 min-w-12 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            ▶
          </button>
        </div>

        {/* 우측 버튼들 */}
        <div className="flex items-center gap-1 sm:gap-2">
          {/* 뷰 모드 전환 버튼 */}
          {onViewModeChange && (
            <button
              onClick={() => onViewModeChange(viewMode === 'list' ? 'calendar' : 'list')}
              className="flex items-center justify-center min-h-12 min-w-12 rounded-lg transition-colors text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label={viewMode === 'list' ? '달력 뷰로 전환' : '리스트 뷰로 전환'}
            >
              {viewMode === 'list' ? (
                /* 달력 아이콘 */
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              ) : (
                /* 리스트 아이콘 */
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          )}

          {/* 필터 버튼 */}
          {onFilterClick && (
            <button
              onClick={onFilterClick}
              className={`flex items-center justify-center min-h-12 min-w-12 rounded-lg transition-colors ${
                hasActiveFilters
                  ? `${THEME_COLORS.accent.background} ${THEME_COLORS.accent.text} ${THEME_COLORS.accent.hover}`
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              aria-label="필터 열기"
            >
              {/* 깔때기 아이콘 */}
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
